
import { useState, useEffect, useLayoutEffect, useRef, Component } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { getAuthHeaders, hasToken, API, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  ArrowLeft, ArrowRight, Share2, Calendar, MapPin, Users, Edit, Save, X,
  Clock, Gift, Book, Music, Image as ImageIcon, Video, Shirt, Hotel, Baby, Car,
  Heart, ChevronRight, ChevronDown, ExternalLink, Zap, Trash2, UserCheck, UserX,
  Settings, Camera, Trophy, Target, Snowflake, FileText, Briefcase,
  DollarSign, ListChecks, Lightbulb, CheckSquare, Box, HelpCircle, CheckCircle2,
  UtensilsCrossed, UserPlus, HeartHandshake, BookOpen, Star, Mic, Eye, EyeOff,
  Mail, MessageCircle, Copy, Activity, Plus, Check, MessageSquare, Upload,
  Play, Pause, AlertCircle, Flower, PlayCircle, Sparkles, LayoutGrid, BarChart3,
  TrendingUp, Search, UserMinus, Send, UserCircle, Download, CalendarClock
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds, formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import GuestManagement from "@/components/GuestManagement";
import GuestViewFloatingButton from "@/components/GuestViewFloatingButton";
import { isFeatureEnabled, ORGANISATIONS_FEATURES, MITMACHEN_FEATURES, MEDIEN_FEATURES, ALL_FEATURES_NEW } from "@/config/featureGroupsNew";

// Import feature components - TEMPORARILY DISABLED TO ISOLATE CRASH

import EventPlaylist from "@/components/EventPlaylist";
import EventWishlist from "@/components/EventWishlist";
import EventTimeline from "@/components/EventTimeline";
import EventCarpool from "@/components/EventCarpool";
import EventTaskList from "@/components/EventTaskList";
import EventBudget from "@/components/EventBudget";
import EventTips from "@/components/EventTips";
import EventGuestbook from "@/components/EventGuestbook";
import EventLocation from "@/components/EventLocation";
import EventDresscode from "@/components/EventDresscode";
import EventVideoUpload from "@/components/EventVideoUpload";
import EventAudioUpload from "@/components/EventAudioUpload";
import SeatingPlan from "@/components/SeatingPlan";
import SelfieGame from "@/components/SelfieGame";
import Gamification from "@/components/Gamification";
import MatchingTool from "@/components/MatchingTool";
import TeamMissions from "@/components/TeamMissions";
import EmployeeSpotlight from "@/components/EmployeeSpotlight";
import WorkshopDocuments from "@/components/WorkshopDocuments";
import BucketList from "@/components/BucketList";
import DigitalYearbook from "@/components/DigitalYearbook";
import DishVoting from "@/components/DishVoting";
import NameAdvisor from "@/components/NameAdvisor";
import YearReview from "@/components/YearReview";
import SecretSantaGenerator from "@/components/SecretSantaGenerator";
import MemoryBox from "@/components/MemoryBox";
import GenderGuessGame from "@/components/GenderGuessGame";
import VisionBoard from "@/components/VisionBoard";
import EventPhotoUpload from "@/components/EventPhotoUpload";
import MediaDownloadCenter from "@/components/MediaDownloadCenter";


const authConfig = () => ({
  withCredentials: !hasToken(),
  headers: getAuthHeaders()
});

const THEME = {
  bg: "#121212",
  bgSubtle: "var(--brand-text)",
  bgCard: "#242424",
  accent: "var(--brand-accent)",
  accentSubtle: "var(--brand-accent)22",
  text: "#FFFFFF",
  textMuted: "#A0A0A0",
  fontSerif: "'Cormorant Garamond', serif",
  fontSans: "'Plus Jakarta Sans', sans-serif"
};

const ICON_MAP = {
  Briefcase, Hotel, Baby, MapPin, Users, Shirt, Clock, Star, Book, DollarSign,
  ListChecks, CheckSquare, Lightbulb, Gift, FileText, Trophy,
  Camera, Heart, UtensilsCrossed, UserPlus, Calendar, Snowflake, Box,
  HeartHandshake, HelpCircle, Target, Car, BookOpen, Video, Music, Mic, Image: ImageIcon,
  UserCircle, MessageSquare, Download, CalendarClock
};

const COMPONENT_MAP = {
  playlist: EventPlaylist,
  wishlist: EventWishlist,
  timeline: EventTimeline,
  carpool: EventCarpool,
  tasks: EventTaskList,
  budget: EventBudget,
  tips: EventTips,
  guestbook: EventGuestbook,
  location: EventLocation,
  dresscode: EventDresscode,
  video_upload: EventVideoUpload,
  audio_upload: EventAudioUpload,
  seating_plan: SeatingPlan,
  selfie_game: SelfieGame,
  gamification: Gamification,
  matching: MatchingTool,
  team_missions: TeamMissions,
  employee_spotlight: EmployeeSpotlight,
  workshop_docs: WorkshopDocuments,
  bucket_list: BucketList,
  digital_yearbook: DigitalYearbook,
  dish_voting: DishVoting,
  name_advisor: NameAdvisor,
  year_review: YearReview,
  secret_santa: SecretSantaGenerator,
  memory_box: MemoryBox,
  gender_guess: GenderGuessGame,
  vision_board: VisionBoard,
  photo_upload: EventPhotoUpload,
  media_download: MediaDownloadCenter,
  guest_who: GuestManagement,
};

const NotificationBadge = ({ count, className = "" }) => {
  if (!count || count <= 0) return null;
  return (
    <span className={`absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] bg-brand-accent text-black text-[10px] font-black rounded-full flex items-center justify-center px-1.5 shadow-lg border-2 border-[#121212] z-10 ${className}`}>
      {count > 99 ? '99+' : count}
    </span>
  );
};

const EVENT_TYPE_LABELS = {
  // English Defaults
  wedding: { label: "Hochzeit", emoji: "💍", image: "/images/headers/wedding.png" },
  birthday: { label: "Geburtstag", emoji: "🎂", image: "/images/headers/birthday_new.png" },
  party: { label: "Party", emoji: "🎉", image: "/images/headers/party_new.png" },
  bachelorette: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  graduation: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  anniversary: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  babyshower: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  baby_shower: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  baby: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  corporate: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  christmas: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  newyear: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  new_year: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  easter: { label: "Ostern", emoji: "🐰", image: "/images/headers/easter.png" },
  valentine: { label: "Valentinstag", emoji: "💖", image: "/images/headers/valentine.png" },

  // German & Aliases
  hochzeit: { label: "Hochzeit", emoji: "💍", image: "/images/headers/wedding.png" },
  geburtstag: { label: "Geburtstag", emoji: "🎂", image: "/images/headers/birthday_new.png" },
  feier: { label: "Party", emoji: "🎉", image: "/images/headers/party_new.png" },
  jga: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  junggesellenabschied: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  abschluss: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  abschlussfeier: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  jubilaeum: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  jubiläum: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  babyparty: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  taufe: { label: "Taufe", emoji: "✝️", image: "/images/headers/baby-shower.png" },
  gender_reveal: { label: "Gender Reveal", emoji: "👶", image: "/images/headers/baby-shower.png" },
  firma: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  firmenevent: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  business: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  weihnachten: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  weihnachtsfeier: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  silvester: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  ostern: { label: "Ostern", emoji: "🐰", image: "/images/headers/easter.png" },
  valentinstag: { label: "Valentinstag", emoji: "💖", image: "/images/headers/valentine.png" },

  other: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" },
  sonstiges: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" },
  individuell: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" }
};

const safelyFormatDate = (dateString, formatStr) => {
  try {
    if (!dateString) return "";
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return "";
    return format(date, formatStr, { locale: de });
  } catch (e) {
    console.error("Date formatting error:", e);
    return "";
  }
};

// Safe fallbacks for potential missing dependencies
const StatusIcon = () => null;
const getTaskStatus = () => "pending";
const isSameDay = () => false;

const safelyFormatTimeAgo = (dateString) => {
  try {
    if (!dateString) return "";
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return "";
    return formatDistanceToNow(date, { addSuffix: true, locale: de });
  } catch (e) {
    return "";
  }
};

const GlobalBackgroundComponent = ({ heroImage, event, setHeroImage }) => (
  <div className="fixed inset-0 z-0 overflow-hidden bg-white">
    {heroImage && (
      <img
        src={heroImage}
        className="w-full h-full object-cover blur-[20px] scale-110 opacity-[0.12] grayscale"
        alt=""
        onError={(e) => {
          const typeKey = event?.event_type?.toLowerCase() || 'other';
          const typeInfo = EVENT_TYPE_LABELS[typeKey] || EVENT_TYPE_LABELS.other;
          if (e.target.src !== typeInfo.image) {
            e.target.src = typeInfo.image;
            if (setHeroImage) setHeroImage(typeInfo.image);
          } else if (e.target.src !== "/images/headers/other.png") {
            e.target.src = "/images/headers/other.png";
          }
        }}
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-tr from-white via-white/40 to-brand-accent/5" />
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--brand-accent-hsl)_0.05,transparent_0.2)]" />
  </div>
);

function FeatureDetailView({ feature, event, onBack, isOwner, Background }) {
  const Comp = COMPONENT_MAP[feature.key];
  return (
    <div className="min-h-screen bg-white md:bg-brand-background text-brand-text font-['Plus_Jakarta_Sans'] relative pb-0 md:pb-40">
      <div className="hidden md:block">{Background && <Background />}</div>
      <div className="relative z-10 p-0 md:p-12 max-w-4xl mx-auto space-y-0 md:space-y-10">

        {/* Header - Sticky on Mobile */}
        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md md:bg-transparent md:backdrop-blur-none md:relative flex items-center gap-6 md:mb-4 px-6 py-4 md:px-0 md:pt-0 border-b md:border-none border-gray-100">
          <button onClick={onBack} className="w-10 h-10 md:w-14 md:h-14 bg-white/50 md:bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm md:shadow-xl hover:bg-brand-accent transition-all group shrink-0">
            <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-brand-text group-hover:text-white group-hover:scale-110 transition-transform" />
          </button>
          <div>
            <h1 className="text-xl md:text-4xl font-black tracking-tighter text-brand-text">{feature.label}</h1>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-accent mt-0.5">{event.title}</p>
          </div>
        </div>

        {/* Content - Fullscreen on Mobile */}
        <div className="bg-white md:rounded-[4rem] p-4 md:p-12 md:border border-gray-100 shadow-none md:shadow-2xl min-h-[calc(100vh-80px)] md:min-h-[65vh]">
          {Comp ? <Comp eventId={event.id} isOwner={isOwner} /> : <div className="text-center py-20 opacity-20 uppercase font-black tracking-[0.5em] italic">Modul geladen...</div>}
        </div>
      </div>
    </div>
  );
}

function SectionDetailView({ section, event, features, onBack, onFeatureClick, Background }) {
  const titles = { organisation: 'Planungs Command', mitmachen: 'Spiele-Zentrale', medien: 'Media Snapshot' };
  const subtitles = { organisation: 'Managing Logistics', mitmachen: 'Gäste Interaktion', medien: 'Content Hub' };

  return (
    <div className="min-h-screen bg-white md:bg-brand-background text-brand-text font-['Plus_Jakarta_Sans'] relative pb-0 md:pb-40">
      <div className="hidden md:block">{Background && <Background />}</div>
      <div className="relative z-10 p-0 md:p-12 max-w-4xl mx-auto space-y-0 md:space-y-10">

        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md md:bg-transparent md:backdrop-blur-none md:relative flex items-center gap-6 md:mb-4 px-6 py-4 md:px-0 md:pt-0 border-b md:border-none border-gray-100">
          <button onClick={onBack} className="w-10 h-10 md:w-14 md:h-14 bg-white/50 md:bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm md:shadow-xl hover:bg-brand-accent transition-all group shrink-0">
            <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-brand-text group-hover:text-white transition-all shadow-xl" />
          </button>
          <div>
            <h1 className="text-xl md:text-4xl font-black tracking-tighter text-brand-text">{titles[section] || 'Sektion'}</h1>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-accent mt-0.5">{subtitles[section] || 'Details'}</p>
          </div>
        </div>

        <div className="bg-white md:bg-transparent md:rounded-none p-4 md:p-0 min-h-screen md:min-h-0">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {(features || []).map((f) => {
              const IconComp = ICON_MAP[f.icon] || Plus;
              return (
                <button
                  key={f.key}
                  onClick={() => onFeatureClick(f)}
                  className="bg-gray-50/50 md:bg-white/90 backdrop-blur-xl p-4 md:p-5 rounded-3xl border border-gray-100/50 text-left transition-all hover:translate-y-[-4px] group shadow-sm md:shadow-md hover:shadow-xl hover:bg-white relative overflow-hidden flex flex-col items-start min-h-[140px] md:min-h-[160px]"
                >
                  <div
                    className="w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center mb-4 transition-all border border-gray-100/50 shadow-inner group-hover:scale-110"
                    style={{ backgroundColor: `${f.color}15`, color: f.color }}
                  >
                    <IconComp className="w-5 h-5 md:w-6 md:h-6" />
                  </div>
                  <div className="flex-1 w-full">
                    <h3 className="text-sm font-black tracking-tight mb-1 text-brand-text group-hover:text-brand-accent transition-colors line-clamp-1">{f.label}</h3>
                    <p className="text-[10px] font-medium text-gray-400 leading-tight line-clamp-2">{f.description || "Modul öffnen"}</p>
                  </div>
                  <div className="w-full h-[1px] bg-gray-50 my-3" />
                  <div className="flex items-center justify-between w-full mt-auto">
                    <span className="text-[8px] font-black text-brand-accent uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">Start</span>
                    <ChevronRight className="w-3 h-3 text-brand-accent group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureSettingsView({ event, enabledFeatures, onToggleFeature, onBack, Background }) {
  const settingsGroups = [
    { title: 'Kern Organisation', features: ORGANISATIONS_FEATURES },
    { title: 'Gäste Interaktion', features: MITMACHEN_FEATURES },
    { title: 'Content & Medien', features: MEDIEN_FEATURES }
  ];

  return (
    <div className="min-h-screen bg-white md:bg-brand-background text-brand-text font-['Plus_Jakarta_Sans'] relative pb-0 md:pb-40">
      <div className="hidden md:block">{Background && <Background />}</div>
      <div className="relative z-10 p-0 md:p-12 max-w-4xl mx-auto space-y-0 md:space-y-10">

        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md md:bg-transparent md:backdrop-blur-none md:relative flex items-center gap-6 md:mb-4 px-6 py-4 md:px-0 md:pt-0 border-b md:border-none border-gray-100">
          <button onClick={onBack} className="w-10 h-10 md:w-14 md:h-14 bg-white/50 md:bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm md:shadow-xl hover:bg-brand-accent transition-all group shrink-0">
            <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-brand-text group-hover:text-white transition-all" />
          </button>
          <div>
            <h1 className="text-xl md:text-4xl font-black tracking-tighter">Feature Cockpit</h1>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-accent mt-0.5">Status Management</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-0 md:gap-12 bg-white md:bg-transparent min-h-screen md:min-h-0 pt-0 px-0 md:pt-6 md:p-0">
          {settingsGroups.map((group) => (
            <div key={group.title} className="bg-transparent md:bg-white md:rounded-[4rem] p-6 md:p-12 border-b md:border border-gray-100 last:border-0 shadow-none md:shadow-2xl mb-0 md:mb-0">
              <h3 className="text-lg md:text-2xl font-black uppercase tracking-[0.3em] text-brand-accent mb-6 md:mb-12 border-b border-gray-50 pb-4 md:pb-6">{group.title}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4 md:gap-y-8">
                {(group.features || []).map((f) => (
                  <div key={f.key} className="flex items-center justify-between p-4 md:p-6 bg-gray-50 rounded-2xl md:rounded-3xl border border-gray-100 items-center">
                    <div className="flex items-center gap-4 md:gap-5">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-xl md:rounded-2xl flex items-center justify-center border border-gray-100 p-2">
                        {(() => {
                          const IconComponent = (f.icon && ICON_MAP[f.icon]) ? ICON_MAP[f.icon] : Sparkles;
                          return <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-brand-accent" />;
                        })()}
                      </div>
                      <span className="text-sm md:text-base font-bold text-brand-text">{f.label}</span>
                    </div>
                    <Switch
                      checked={!!enabledFeatures[f.key]}
                      onCheckedChange={(checked) => onToggleFeature(f.key, checked)}
                      className="data-[state=checked]:bg-brand-accent scale-75 md:scale-100 origin-right"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function EventDetailHeroNewContent({ user: propUser, onLogout }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [guests, setGuests] = useState([]);
  const [activeView, setActiveView] = useState('main');
  const [activeFeature, setActiveFeature] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [suggestedFavorites, setSuggestedFavorites] = useState([]);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [user, setUser] = useState(propUser);
  const [newCounts, setNewCounts] = useState({ guests: 2, tasks: 1 });
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [tasks, setTasks] = useState([]);
  // Cache buster for images
  const [cacheBuster] = useState(Date.now());
  const [heroImage, setHeroImage] = useState("https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80");

  const [editingTitle, setEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState("");

  const [editingDate, setEditingDate] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingType, setEditingType] = useState(false);

  const [tempDate, setTempDate] = useState("");
  const [tempLocation, setTempLocation] = useState("");
  const [tempType, setTempType] = useState("");

  const isOwner = user?.id && event?.user_id ? user.id === event.user_id : false;

  useEffect(() => {
    if (propUser) {
      setUser(propUser);
    } else {
      const fetchUser = async () => {
        try {
          const response = await axios.get(`${API}/auth/me`, authConfig());
          setUser(response.data);
        } catch (error) { }
      };
      fetchUser();
    }
  }, [propUser]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [activeView, activeFeature]);

  useEffect(() => {
    fetchEvent();
    fetchGuests();
    fetchRecentActivities();
    fetchSuggestedFavorites();
    fetchRecentPhotos();
    fetchTasks();

    const viewParam = searchParams.get('view');
    const featureParam = searchParams.get('feature');

    if (viewParam) {
      if (['organisation', 'mitmachen', 'medien', 'guests', 'settings'].includes(viewParam)) {
        setActiveView(viewParam);
      } else if (ALL_FEATURES_NEW[viewParam]) {
        const featureObj = [...ORGANISATIONS_FEATURES, ...MITMACHEN_FEATURES, ...MEDIEN_FEATURES].find(f => f.key === viewParam);
        if (featureObj) setActiveFeature(featureObj);
      }
    } else if (featureParam) {
      const featureObj = [...ORGANISATIONS_FEATURES, ...MITMACHEN_FEATURES, ...MEDIEN_FEATURES].find(f => f.key === featureParam);
      if (featureObj) setActiveFeature(featureObj);
    }
  }, [eventId, searchParams]);

  const fetchRecentPhotos = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/photos?limit=8`, authConfig());
      setRecentPhotos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setRecentPhotos([]);
    }
  };

  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Bild ist zu groß (Max 10MB)");
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/events/${eventId}/cover`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders()
        }
      });

      const newUrl = res.data.cover_image;
      const displayUrl = newUrl.startsWith('http') ? newUrl : `${BACKEND_URL}${newUrl}?v=${Date.now()}`;

      setHeroImage(displayUrl);
      setEvent(prev => ({ ...prev, cover_image: newUrl }));
      toast.success("Hintergrundbild aktualisiert");
    } catch (error) {
      console.error(error);
      toast.error("Fehler beim Hochladen: " + (error.response?.data?.detail || error.message));
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/tasks`, authConfig());
      const data = Array.isArray(response.data) ? response.data : [];
      setTasks(data.filter(t => !t.completed).slice(0, 5));
    } catch (error) {
      setTasks([]);
    }
  };

  const fetchSuggestedFavorites = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/favorites`, authConfig());
      setSuggestedFavorites(response.data.suggested_favorites || []);
    } catch (error) { }
  };

  const fetchRecentActivities = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/activities?limit=10`, authConfig());
      setRecentActivities(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setRecentActivities([]);
    }
  };

  useEffect(() => {
    if (event) {
      const updateCountdown = () => {
        try {
          if (!event.date) return;
          const eventDate = parseISO(event.date);
          if (isNaN(eventDate.getTime())) return;

          const now = new Date();
          if (eventDate > now) {
            setCountdown({
              days: Math.max(0, differenceInDays(eventDate, now)),
              hours: Math.max(0, differenceInHours(eventDate, now) % 24),
              minutes: Math.max(0, differenceInMinutes(eventDate, now) % 60),
              seconds: Math.max(0, differenceInSeconds(eventDate, now) % 60)
            });
          }
        } catch (e) { console.error(e); }
      };
      updateCountdown();
      const timer = setInterval(updateCountdown, 1000);
      return () => clearInterval(timer);
    }
  }, [event]);

  const fetchGuests = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/guests`, authConfig());
      setGuests(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setGuests([]);
    }
  };

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}`, authConfig());
      setEvent(response.data);
      setTempTitle(response.data.title);
      setTempDate(response.data.date ? safelyFormatDate(response.data.date, "yyyy-MM-dd'T'HH:mm") : "");
      setTempLocation(response.data.location || "");
      setTempType(response.data.event_type || "other");
      if (response.data.cover_image) {
        let path = response.data.cover_image;
        if (path.startsWith('data:')) {
          setHeroImage(path);
        } else {
          if (path.includes('uploads/')) {
            const parts = path.split('uploads/');
            path = `/api/media/${parts[parts.length - 1]}`;
          }
          if (!path.startsWith('/') && !path.startsWith('http')) {
            path = `/${path}`;
          }
          let coverImageUrl = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;

          // Add cache buster
          if (coverImageUrl.includes('/api/media/')) {
            coverImageUrl = `${coverImageUrl}?v=${cacheBuster}`;
          }

          setHeroImage(coverImageUrl);
        }
      } else {
        const typeKey = response.data.event_type?.toLowerCase() || 'other';
        const typeInfo = EVENT_TYPE_LABELS[typeKey] || EVENT_TYPE_LABELS.other;
        setHeroImage(typeInfo?.image || EVENT_TYPE_LABELS.other.image);
      }
      const featuresRes = await axios.get(`${API}/events/${eventId}/enabled-features`, authConfig());
      setEnabledFeatures(featuresRes.data.features || {});
    } catch (error) {
      toast.error("Event konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveField = async (field, value) => {
    try {
      if (hasToken()) {
        const payload = { [field]: value };
        await axios.put(`${API}/events/${event.id}`, payload, authConfig());
        setEvent(prev => ({ ...prev, [field]: value }));
        toast.success("Gespeichert");
        setEditingDate(false);
        setEditingLocation(false);
        setEditingType(false);
      }
    } catch (e) {
      console.error(e);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleSaveTitle = async () => {
    try {
      if (hasToken()) {
        await axios.put(`${API}/events/${event.id}`, { title: tempTitle }, authConfig());
        setEvent(prev => ({ ...prev, title: tempTitle }));
        setEditingTitle(false);
        toast.success("Titel gespeichert");
      }
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  const toggleFeature = async (featureKey, enabled) => {
    try {
      await axios.post(`${API}/events/${eventId}/features/toggle`, { feature_key: featureKey, enabled }, authConfig());
      setEnabledFeatures(prev => ({ ...prev, [featureKey]: enabled }));
      toast.success("Einstellung gespeichert");
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/e/${event.share_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Link kopiert!");
    setShowShareMenu(false);
  };

  const openGoogleMaps = () => {
    if (event?.location) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`, '_blank');
    }
  };

  const shareViaWhatsApp = () => {
    const url = `${window.location.origin}/e/${event.share_code}`;
    const text = `Hey! Schau dir mein Event "${event.title}" auf Mokiplaner an: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareViaEmail = () => {
    const url = `${window.location.origin}/e/${event.share_code}`;
    const subject = encodeURIComponent(`Einladung zu ${event.title}`);
    const body = encodeURIComponent(`Hey!\n\nIch lade dich herzlich zu meinem Event "${event.title}" ein.\n\nAlle Details und die Zusage findest du hier:\n${url}\n\nIch freue mich auf dich!`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const canShowFeature = (key) => isFeatureEnabled(key, enabledFeatures);
  const visibleOrganisationFeatures = ORGANISATIONS_FEATURES.filter(f => canShowFeature(f.key));
  const visibleMitmachenFeatures = MITMACHEN_FEATURES.filter(f => canShowFeature(f.key));
  const visibleMedienFeatures = MEDIEN_FEATURES.filter(f => canShowFeature(f.key));

  if (loading) return <div className="min-h-screen bg-[#121212] flex items-center justify-center p-8"><div className="w-10 h-10 border-4 border-brand-accent border-t-transparent rounded-full animate-spin" /></div>;
  if (!event) return <div className="min-h-screen bg-[#121212] flex flex-col items-center justify-center p-8 text-white"><h1 className="text-2xl font-black mb-4">Event nicht gefunden</h1><Button onClick={() => navigate("/dashboard")}>Dashboard</Button></div>;

  const typeKey = event.event_type?.toLowerCase() || 'other';
  const eventType = EVENT_TYPE_LABELS[typeKey] || EVENT_TYPE_LABELS.other;
  const totalConfirmed = (guests || []).filter(g => g.status === "confirmed").reduce((sum, g) => sum + 1 + (g.plus_one || 0), 0);
  const totalPending = (guests || []).filter(g => !g.status || g.status === "pending").length;

  if (activeFeature) {
    return (
      <FeatureDetailView
        feature={activeFeature}
        event={event}
        onBack={() => setActiveFeature(null)}
        isOwner={true}
        Background={() => <GlobalBackgroundComponent heroImage={heroImage} event={event} setHeroImage={setHeroImage} />}
      />
    );
  }

  if (activeView === 'guests') return (
    <div className="min-h-screen bg-white md:bg-brand-background text-brand-text font-['Plus_Jakarta_Sans'] relative pb-0 md:pb-40">
      <div className="hidden md:block"><GlobalBackgroundComponent heroImage={heroImage} event={event} setHeroImage={setHeroImage} /></div>
      <div className="relative z-10 p-0 md:p-12 max-w-4xl mx-auto space-y-0 md:space-y-10">

        <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md md:bg-transparent md:backdrop-blur-none md:relative flex items-center gap-6 md:mb-4 px-6 py-4 md:px-0 md:pt-0 border-b md:border-none border-gray-100">
          <button onClick={() => setActiveView('main')} className="w-10 h-10 md:w-14 md:h-14 bg-white/50 md:bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm md:shadow-xl hover:bg-brand-accent transition-all group shrink-0">
            <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-brand-text group-hover:text-white transition-all shadow-xl" />
          </button>
          <div>
            <h1 className="text-xl md:text-4xl font-black tracking-tighter">Gästeliste</h1>
            <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-accent mt-0.5">{totalConfirmed} Bestätigte Zusagen</p>
          </div>
        </div>

        <div className="bg-white md:rounded-[4rem] p-4 md:p-12 md:border border-gray-100 shadow-none md:shadow-2xl min-h-[calc(100vh-80px)] md:min-h-0">
          <GuestManagement
            eventId={eventId}
            guests={Array.isArray(guests) ? guests : []}
            onGuestAdded={fetchGuests}
            onGuestUpdated={fetchGuests}
            onGuestDeleted={fetchGuests}
            shareLink={`${window.location.origin}/e/${event.share_code}`}
            eventTitle={event.title}
            isDark={true}
          />
        </div>
      </div>
    </div>
  );

  if (activeView === 'settings') return (
    <FeatureSettingsView
      event={event}
      enabledFeatures={enabledFeatures}
      onToggleFeature={toggleFeature}
      onBack={() => setActiveView('main')}
      Background={() => <GlobalBackgroundComponent heroImage={heroImage} event={event} setHeroImage={setHeroImage} />}
    />
  );
  if (['organisation', 'mitmachen', 'medien'].includes(activeView)) {
    const sectionFeatures = activeView === 'organisation' ? visibleOrganisationFeatures :
      activeView === 'mitmachen' ? visibleMitmachenFeatures :
        visibleMedienFeatures;
    return (
      <SectionDetailView
        section={activeView}
        event={event}
        features={sectionFeatures}
        onBack={() => setActiveView('main')}
        onFeatureClick={setActiveFeature}
        Background={() => <GlobalBackgroundComponent heroImage={heroImage} event={event} setHeroImage={setHeroImage} />}
      />
    );
  }

  return (
    <div className="min-h-screen bg-brand-background text-brand-text font-['Plus_Jakarta_Sans'] relative overflow-x-hidden antialiased pb-40 selection:bg-brand-accent/20">
      <GlobalBackgroundComponent heroImage={heroImage} event={event} setHeroImage={setHeroImage} />

      {/* NEW HERO SECTION (Owner View - Light Mode) */}
      <section className="relative h-screen flex flex-col justify-between px-6 pt-6 pb-24 md:pb-6 overflow-hidden">
        {/* Full container background image */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            className="w-full h-full object-cover"
            alt="Background"
            onError={(e) => {
              const typeKey = event?.event_type?.toLowerCase() || 'other';
              const typeInfo = EVENT_TYPE_LABELS[typeKey] || EVENT_TYPE_LABELS.other;
              if (e.target.src !== typeInfo.image) {
                e.target.src = typeInfo.image;
                setHeroImage(typeInfo.image);
              }
            }}
          />
          <div className="absolute inset-0 bg-white/30 backdrop-blur-[1px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-brand-background/90" />
        </div>

        {/* Edit Background Button (Owner Only) */}
        {isOwner && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              className="hidden"
              accept="image/*"
            />
            <div className="absolute top-6 right-6 z-50 flex gap-2">
              <button
                onClick={() => fileInputRef.current.click()}
                className="px-4 py-2 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-all shadow-lg flex items-center gap-2 group border border-white/20"
                title="Hintergrundbild ändern"
              >
                <ImageIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="text-xs font-bold uppercase tracking-wider">Hintergrund</span>
              </button>
              <button
                onClick={() => window.open(`/e/${event.share_code}`, '_blank')}
                className="px-4 py-2 bg-brand-accent hover:bg-yellow-500 backdrop-blur-md rounded-full text-white transition-all shadow-lg flex items-center gap-2 group border border-white/20 hover:scale-105 active:scale-95"
                title="Zur Gästeansicht wechseln"
              >
                <Eye className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="text-xs font-bold uppercase tracking-wider">Gästeansicht</span>
              </button>
            </div>
          </>
        )}

        {/* TOP CONTENT: Timer & Title */}
        <div 
          className="relative z-20 w-full flex flex-col items-center pt-2 md:pt-8 gap-2 md:gap-4 transition-transform duration-300"
          style={{ transform: `translateY(${event?.title_position_y || 0}px)` }}
        >

          {/* Countdown Timer */}
          <div className="flex gap-4 md:gap-6 mb-0 animate-in fade-in slide-in-from-top-4 duration-700">
            {['Tage', 'Stunden', 'Minuten'].map((label, i) => {
              const value = i === 0 ? countdown.days : i === 1 ? countdown.hours : countdown.minutes;
              return (
                <div key={label} className="flex flex-col items-center">
                  <span className="text-3xl font-serif font-medium text-brand-text leading-none drop-shadow-sm">
                    {value < 10 ? `0${value}` : value}
                  </span>
                  <span className="text-[7px] font-black uppercase tracking-[0.4em] text-brand-text opacity-70 mt-1">{label}</span>
                </div>
              )
            })}
          </div>

          {/* Top Badges (Result of compacting) */}
          <div className="flex items-center justify-center gap-3 opacity-100 transition-opacity">
            <span className="px-4 py-2 bg-white/90 backdrop-blur-md text-brand-text rounded-full text-[10px] font-black uppercase tracking-[0.2em] border border-white/30 shadow-lg">
              Creator
            </span>
            <button
              onClick={() => window.open(`/e/${event.share_code}`, '_blank')}
              className="px-5 py-2 bg-brand-accent hover:bg-yellow-500 text-white rounded-full text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-2 transition-all shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
            >
              <Eye className="w-3.5 h-3.5" /> Gästeansicht
            </button>
          </div>

          {/* Title (Editable) - Moved Up */}
          <div className="relative group mt-2 max-w-4xl mx-auto text-center">
            {editingTitle ? (
              <div className="flex flex-col sm:flex-row gap-4 items-center animate-in fade-in zoom-in-95 duration-200">
                <input
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  className="text-4xl md:text-7xl font-medium text-center bg-white/60 backdrop-blur-xl border border-brand-accent rounded-2xl text-brand-text p-2 w-full min-w-[300px] focus:outline-none shadow-2xl"
                  style={{ fontFamily: THEME.fontSerif }}
                  autoFocus
                />
                <div className="flex gap-2">
                  <button onClick={handleSaveTitle} className="w-10 h-10 bg-brand-accent rounded-xl flex items-center justify-center text-white shadow-lg hover:bg-[#a07e54] transition-all"><Check className="w-5 h-5" /></button>
                  <button onClick={() => setEditingTitle(false)} className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-gray-400 shadow-lg hover:text-red-500 transition-all"><X className="w-5 h-5" /></button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <h1
                  onClick={() => { setEditingTitle(true); setTempTitle(event.title); }}
                  className="text-4xl md:text-8xl lg:text-9xl font-medium tracking-tight text-brand-text cursor-pointer hover:text-brand-accent transition-all duration-500 drop-shadow-xl leading-[0.9]"
                  style={{
                    fontFamily: THEME.fontSerif,
                    textShadow: '0 2px 20px rgba(255,255,255,0.4), 0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  {event.title}
                </h1>
                <button
                  onClick={() => { setEditingTitle(true); setTempTitle(event.title); }}
                  className="absolute -top-4 -right-8 p-2 bg-white/50 hover:bg-white rounded-full text-gray-400 hover:text-brand-accent transition-all opacity-0 group-hover:opacity-100"
                >
                  <Edit className="w-3 h-3" />
                </button>
                {event.hero_title && (
                  <p className="text-[9px] font-bold uppercase tracking-[0.4em] text-brand-accent mt-2 opacity-80">{event.hero_title}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* BOTTOM CONTENT: Info Grid */}
        <div className="relative z-10 w-full flex flex-col items-center pb-0">
          {/* Subtle Scroll Hint */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="flex flex-col items-center gap-1 mb-4 cursor-pointer group"
            onClick={() => window.scrollTo({ top: window.innerHeight * 0.9, behavior: 'smooth' })}
          >
            <span className="text-xs font-black uppercase tracking-[0.4em] text-brand-text opacity-60 group-hover:opacity-100 transition-opacity">Scroll</span>
            <ChevronDown className="w-6 h-6 text-brand-text animate-bounce opacity-80 group-hover:opacity-100" />
          </motion.div>

          {/* Info Grid - Ultra Compact & Bottom Aligned */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 w-full max-w-[550px] px-2 mb-4">
            {/* Date (Spans 2 cols on mobile, 1 on desktop) */}
            <div className={`bg-white border border-gray-100 p-1.5 rounded-[1rem] shadow-md flex flex-col items-center justify-center gap-0.5 transform transition-all relative group h-14 ${editingDate ? 'z-50' : 'hover:scale-[1.02]'}`}>
              {editingDate ? (
                <div className="flex items-center gap-1 w-full px-2">
                  <input
                    type="datetime-local"
                    value={tempDate}
                    onChange={(e) => setTempDate(e.target.value)}
                    className="w-full text-xs bg-gray-50 border-none focus:outline-none text-center font-bold rounded-md py-1"
                  />
                  <button onClick={() => handleSaveField('date', tempDate)} className="p-1.5 bg-brand-accent rounded-full text-white"><Check className="w-2.5 h-2.5" /></button>
                  <button onClick={() => setEditingDate(false)} className="p-1 px-2 text-red-500 rounded-full"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <>
                  <span className="text-[7px] font-black text-brand-accent uppercase tracking-[0.3em]">Datum</span>
                  <p className="text-base font-black text-brand-text uppercase leading-none">
                    {safelyFormatDate(event.date, "dd. MMM")}
                  </p>
                  <p className="text-[9px] font-black text-gray-500">{safelyFormatDate(event.date, "HH:mm")} Uhr</p>
                  <button
                    onClick={() => {
                      setTempDate(event.date ? safelyFormatDate(event.date, "yyyy-MM-dd'T'HH:mm") : "");
                      setEditingDate(true);
                    }}
                    className="absolute -top-2 -right-2 p-1.5 bg-brand-accent text-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Edit className="w-2.5 h-2.5" />
                  </button>
                </>
              )}
            </div>

            {/* Location */}
            <div className={`bg-brand-text backdrop-blur-xl border border-black/5 p-1.5 rounded-[1rem] shadow-sm flex flex-col items-center justify-center gap-0.5 transform transition-all relative group h-14 ${editingLocation ? 'z-50 px-2' : 'hover:scale-[1.02] cursor-pointer hover:bg-brand-accent'}`}
              onClick={(e) => { if (!editingLocation && !e.target.closest('button')) openGoogleMaps(); }}
            >
              {editingLocation ? (
                <div className="flex items-center gap-1 w-full">
                  <input
                    type="text"
                    value={tempLocation}
                    onChange={(e) => setTempLocation(e.target.value)}
                    className="w-full text-[10px] bg-white/10 text-white border-none focus:outline-none text-center font-bold rounded-md py-1"
                    autoFocus
                  />
                  <button onClick={() => handleSaveField('location', tempLocation)} className="p-1.5 bg-brand-accent rounded-full text-white shrink-0"><Check className="w-2.5 h-2.5" /></button>
                  <button onClick={() => setEditingLocation(false)} className="p-1 px-2 text-white/50 hover:text-white shrink-0"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <>
                  <span className="text-[7px] font-black text-brand-accent uppercase tracking-[0.3em] group-hover:text-white transition-colors">Location</span>
                  <div className="flex items-center gap-1.5 text-white group-hover:text-white transition-colors">
                    <MapPin className="w-3 h-3 text-brand-accent group-hover:text-white transition-colors" />
                    <p className="text-[10px] font-black uppercase truncate max-w-[90px]">{event.location?.split(',')[0] || 'Berlin'}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTempLocation(event.location || "");
                      setEditingLocation(true);
                    }}
                    className="absolute -top-2 -right-2 p-1.5 bg-white text-brand-text rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:text-brand-accent"
                  >
                    <Edit className="w-2.5 h-2.5" />
                  </button>
                </>
              )}
            </div>

            {/* Type */}
            <div className={`bg-brand-text backdrop-blur-xl border border-black/5 p-1.5 rounded-[1rem] shadow-sm flex flex-col items-center justify-center gap-0.5 transform transition-all relative group h-14 ${editingType ? 'z-50 px-2' : 'hover:scale-[1.02] hover:bg-brand-accent'}`}>
              {editingType ? (
                <div className="flex items-center gap-1 w-full">
                  <select
                    value={tempType}
                    onChange={(e) => setTempType(e.target.value)}
                    className="w-full text-[10px] bg-white/10 text-white border-none focus:outline-none text-center font-bold rounded-md py-1 appearance-none"
                  >
                    {Object.entries(EVENT_TYPE_LABELS).map(([key, { label }]) => (
                      <option key={key} value={key} className="text-black">{label}</option>
                    ))}
                  </select>
                  <button onClick={() => handleSaveField('event_type', tempType)} className="p-1.5 bg-brand-accent rounded-full text-white shrink-0"><Check className="w-2.5 h-2.5" /></button>
                  <button onClick={() => setEditingType(false)} className="p-1 px-2 text-white/50 hover:text-white shrink-0"><X className="w-3 h-3" /></button>
                </div>
              ) : (
                <>
                  <span className="text-[7px] font-black text-brand-accent uppercase tracking-[0.3em] group-hover:text-white transition-colors">Event Typ</span>
                  <div className="flex items-center gap-1.5 text-white group-hover:text-white transition-colors">
                    <Sparkles className="w-3 h-3 text-brand-accent group-hover:text-white transition-colors" />
                    <p className="text-[10px] font-black uppercase truncate max-w-[90px]">{eventType.label}</p>
                  </div>
                  <button
                    onClick={() => {
                      setTempType(event.event_type || "other");
                      setEditingType(true);
                    }}
                    className="absolute -top-2 -right-2 p-1.5 bg-white text-brand-text rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:text-brand-accent"
                  >
                    <Edit className="w-2.5 h-2.5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Command Dashboard */}
      <main className="relative z-10 px-6 md:px-12 max-w-7xl mx-auto space-y-12 pt-10">

        {/* Key Intelligence Stats (Light) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Gäste', val: totalConfirmed, icon: Users, onClick: () => setActiveView('guests') },
            { label: 'Momente', val: recentPhotos.length, icon: ImageIcon, onClick: () => setActiveView('medien') },
            { label: 'Tasks', val: tasks.length, icon: ListChecks, onClick: () => setActiveView('organisation') },
            { label: 'Tage', val: countdown.days, icon: Clock }
          ].map((stat, i) => (
            <button
              key={i}
              onClick={stat.onClick}
              className="bg-white p-4 rounded-3xl border border-gray-100 flex items-center gap-4 transition-all hover:shadow-xl hover:-translate-y-1 group shadow-sm"
            >
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0 group-hover:bg-brand-accent group-hover:text-white transition-all">
                <stat.icon className="w-5 h-5 text-brand-accent group-hover:text-white" />
              </div>
              <div className="flex flex-col items-start min-w-0">
                <h4 className="text-2xl font-black text-brand-text leading-none">{stat.val}</h4>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1 truncate">{stat.label}</p>
              </div>
            </button>
          ))}
        </div>

        {/* Master Control Hub (Integrated Guest List) */}
        <div className="bg-white rounded-[4rem] p-8 md:p-12 border border-gray-100 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.05)]">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12 border-b border-gray-50 pb-10">
            <div>
              <h2 className="text-4xl font-black text-brand-text tracking-tight" style={{ fontFamily: THEME.fontSans }}>Event Steuerung</h2>
              <p className="text-sm font-bold text-gray-400 mt-2 uppercase tracking-widest">Master Dashboard for {event.title}</p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setShowShareMenu(true)}
                className="bg-brand-accent text-white px-8 py-5 rounded-[2rem] font-black uppercase text-[10px] tracking-[0.3em] flex items-center gap-3 hover:bg-[#a07e54] transition-all shadow-lg shadow-brand-accent/20 active:scale-95"
              >
                <Share2 className="w-5 h-5" /> Einladen
              </button>
              <button
                onClick={() => setActiveView('settings')}
                className="bg-gray-50 text-gray-600 w-14 h-14 rounded-[2rem] flex items-center justify-center border border-gray-100 hover:bg-gray-100 hover:text-brand-text transition-all shadow-lg"
              >
                <Settings className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Left Column: Direct Guest Management */}
            <div className="lg:col-span-8 space-y-8">
              <div className="bg-brand-background rounded-[3.5rem] p-8 md:p-10 border border-gray-50 shadow-sm relative overflow-hidden group">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-gray-100">
                      <Users className="w-6 h-6 text-brand-accent" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-brand-text">Gästeliste</h3>
                      <p className="text-[10px] font-black text-brand-accent uppercase tracking-widest">{totalConfirmed} Bestätigt • {totalPending} Offen</p>
                    </div>
                  </div>
                </div>

                <div className="max-h-[800px] overflow-y-auto no-scrollbar -mx-2 px-2">
                  <GuestManagement
                    eventId={eventId}
                    guests={Array.isArray(guests) ? guests : []}
                    onGuestAdded={fetchGuests}
                    onGuestUpdated={fetchGuests}
                    onGuestDeleted={fetchGuests}
                    shareLink={`${window.location.origin}/e/${event.share_code}`}
                    eventTitle={event.title}
                  />
                </div>
              </div>
            </div>

            {/* Right Column: Active Tasks & Modules */}
            <div className="lg:col-span-4 space-y-8">
              {/* Direct Task Input (If Enabled) */}
              {canShowFeature('tasks') && (
                <div className="bg-white rounded-[2.5rem] p-8 border border-gray-100 shadow-sm space-y-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                      </div>
                      <h4 className="font-black text-brand-text">Deine To-Dos</h4>
                    </div>
                    <button
                      onClick={() => setActiveFeature(ORGANISATIONS_FEATURES.find(f => f.key === 'tasks'))}
                      className="text-[9px] font-black uppercase text-brand-accent hover:text-brand-text tracking-widest"
                    >
                      Alle
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Compact Task List Component Inline */}
                    <div className="max-h-[300px] overflow-y-auto no-scrollbar -mx-2 px-2">
                      <EventTaskList eventId={eventId} isOwner={isOwner} />
                    </div>
                  </div>
                </div>
              )}

              {/* Compact Module Hub */}
              <div className="grid grid-cols-1 gap-4">
                {[
                  { key: 'organisation', title: 'Organisation', features: visibleOrganisationFeatures, icon: LayoutGrid, color: 'var(--brand-accent)' },
                  { key: 'mitmachen', title: 'Entertainment', features: visibleMitmachenFeatures, icon: Trophy, color: '#10B981' },
                  { key: 'medien', title: 'Media Pool', features: visibleMedienFeatures, icon: Video, color: '#3B82F6' }
                ].map((col) => (col.features.length > 0 &&
                  <div key={col.key} className="bg-white rounded-[2.5rem] p-6 border border-gray-100 hover:shadow-lg transition-all group">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center group-hover:bg-brand-background transition-colors">
                          <col.icon className="w-4 h-4" style={{ color: col.color }} />
                        </div>
                        <h4 className="text-xs font-black text-brand-text tracking-tight">{col.title}</h4>
                      </div>
                      <button
                        onClick={() => setActiveView(col.key)}
                        className="text-[8px] font-black uppercase text-brand-accent hover:text-brand-text tracking-widest"
                      >
                        Details
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {col.features.slice(0, 3).map(f => (
                        <button
                          key={f.key}
                          onClick={() => setActiveFeature(f)}
                          className="px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100 text-[10px] font-bold text-gray-600 hover:bg-brand-accent hover:text-white hover:border-brand-accent transition-all"
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Chat Link */}
              <button
                onClick={() => navigate(`/event/${eventId}/chat`)}
                className="w-full bg-brand-text text-white p-6 rounded-[2.5rem] flex items-center justify-between hover:bg-brand-accent transition-all group shadow-xl"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-brand-accent" />
                  </div>
                  <div className="text-left">
                    <p className="font-black text-sm">Community Chat</p>
                    <p className="text-[9px] font-bold text-white/50 group-hover:text-white/80 uppercase tracking-widest leading-none mt-1">Austauschen & Planen</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-brand-accent group-hover:text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Real-time Activity & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Section: Letzte System Aktivitäten */}
          <section className="bg-white rounded-[4.5rem] border border-gray-100 shadow-2xl p-12 space-y-10">
            <h3 className="text-2xl font-black text-brand-text flex items-center gap-4">
              <Activity className="w-8 h-8 text-brand-accent" /> Letzte System Aktivitäten
            </h3>
            <div className="space-y-8 max-h-[400px] overflow-y-auto no-scrollbar pr-4">
              {(Array.isArray(recentActivities) ? recentActivities : []).slice(0, 6).map((act, i) => (
                <div key={i} className="flex gap-6 items-start group">
                  <div className="w-2 md:w-2 h-14 bg-gray-100 rounded-full overflow-hidden relative">
                    <div className="absolute top-0 left-0 w-full bg-brand-accent h-1/2" />
                  </div>
                  <div className="flex-1 pb-6 border-b border-gray-50">
                    <p className="text-[15px] font-bold text-gray-800 group-hover:text-brand-accent transition-colors">{act?.description}</p>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mt-2">
                      {safelyFormatTimeAgo(act?.created_at)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Section: Bereit für den Start? */}
          <section className="bg-brand-text p-12 rounded-[4.5rem] border border-black shadow-2xl flex flex-col justify-center text-center space-y-8 relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-brand-accent/20 via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
            <div className="w-24 h-24 bg-brand-accent/10 rounded-[2.5rem] flex items-center justify-center mx-auto border border-brand-accent/20 mb-4 z-10">
              <Sparkles className="w-12 h-12 text-brand-accent" />
            </div>
            <div className="space-y-4 z-10">
              <h3 className="text-4xl font-black tracking-tighter text-white">Bereit für den Start?</h3>
              <p className="text-base text-white/70 leading-relaxed font-medium">Dein Event ist zu 85% fertig geplant. Teile jetzt den geheimen Link mit deinen Gästen.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6 z-10">
              <button
                onClick={() => setShowShareMenu(true)}
                className="bg-brand-accent text-white px-12 py-7 rounded-[2rem] font-black uppercase text-[11px] tracking-[0.3em] hover:bg-white hover:text-brand-text transition-all shadow-2xl active:scale-95"
              >
                Link kopieren
              </button>
              <button
                onClick={() => navigate(`/e/${event.share_code}`)}
                className="bg-white/10 border border-white/20 text-white px-12 py-7 rounded-[2rem] font-black uppercase text-[11px] tracking-[0.3em] hover:bg-white/20 transition-all active:scale-95"
              >
                Vorschau live
              </button>
            </div>
          </section>
        </div>
      </main>

      {/* Share Dialog Overhaul */}
      <Dialog open={showShareMenu} onOpenChange={setShowShareMenu}>
        <DialogContent className="max-w-xl bg-[#121212]/95 backdrop-blur-[50px] border-white/10 text-white rounded-[5rem] p-16 font-['Plus_Jakarta_Sans'] shadow-[0_50px_100px_-20px_rgba(0,0,0,1)]">
          <DialogTitle className="text-5xl font-black tracking-tighter text-center mb-16 drop-shadow-2xl">Einladen</DialogTitle>
          <div className="grid grid-cols-3 gap-12">
            <button onClick={shareViaWhatsApp} className="flex flex-col items-center gap-6 group">
              <div className="w-24 h-24 bg-[#25D366] rounded-[2.5rem] flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-700"><MessageCircle className="w-12 h-12 text-white fill-white" /></div>
              <span className="text-[11px] font-black uppercase tracking-[0.4em] text-white/40 group-hover:text-white transition-colors">WhatsApp</span>
            </button>
            <button onClick={shareViaEmail} className="flex flex-col items-center gap-6 group">
              <div className="w-24 h-24 bg-white/10 rounded-[2.5rem] flex items-center justify-center border border-white/10 shadow-2xl group-hover:scale-110 transition-transform duration-700"><Mail className="w-12 h-12 text-white" /></div>
              <span className="text-[11px] font-black uppercase tracking-[0.4em] text-white/40 group-hover:text-white transition-colors">E-Mail</span>
            </button>
            <button onClick={copyShareLink} className="flex flex-col items-center gap-6 group">
              <div className="w-24 h-24 bg-white/10 rounded-[2.5rem] flex items-center justify-center border border-white/10 shadow-2xl group-hover:scale-110 transition-transform duration-700"><Copy className="w-12 h-12 text-white" /></div>
              <span className="text-[11px] font-black uppercase tracking-[0.4em] text-white/40 group-hover:text-white transition-colors">Link</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error("Uncaught error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-10 text-red-500 bg-white min-h-screen z-50 relative flex flex-col items-center justify-center text-left">
          <h1 className="text-2xl font-bold mb-4">Something went wrong.</h1>
          <div className="bg-gray-100 p-4 rounded overflow-auto max-w-full">
            <p className="font-mono font-bold text-sm mb-2">{this.state.error && this.state.error.toString()}</p>
            <details className="whitespace-pre-wrap text-xs font-mono text-gray-600">
              <summary>Stack Trace</summary>
              {this.state.errorInfo && this.state.errorInfo.componentStack}
            </details>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function EventDetailHeroNew(props) {
  return (
    <ErrorBoundary>
      <EventDetailHeroNewContent {...props} />
    </ErrorBoundary>
  );
}
